import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { 
  ArrowLeft, Search, RefreshCw, Users, Mail, Phone, 
  CheckCircle, XCircle, AlertTriangle, Trash2, ShieldCheck,
  Calendar, User, CreditCard
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCustomers = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [emailStatus, setEmailStatus] = useState(null);
  const [showEmailWarning, setShowEmailWarning] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ show: false, customer: null, action: '' });
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin/login');
      return;
    }
    loadData();
  }, [navigate]);

  const [reservations, setReservations] = useState([]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [customersRes, emailRes, reservationsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/customers/admin/list`),
        axios.get(`${BACKEND_URL}/api/customers/admin/email-status`),
        axios.get(`${BACKEND_URL}/api/reservations/`).catch(() => ({ data: [] }))
      ]);
      
      setCustomers(customersRes.data);
      setEmailStatus(emailRes.data);
      setReservations(reservationsRes.data || []);
      
      if (emailRes.data.is_test_mode) {
        setShowEmailWarning(true);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar clientes.' });
    } finally {
      setLoading(false);
    }
  };

  // Buscar reservas do cliente pelo CPF
  const getCustomerReservations = (cpf) => {
    const cpfClean = cpf?.replace(/\D/g, '');
    return reservations.filter(r => r.customer_cpf?.replace(/\D/g, '') === cpfClean);
  };

  // Verificar status de pagamento do cliente
  const getCustomerPaymentStatus = (cpf) => {
    const customerReservations = getCustomerReservations(cpf);
    if (customerReservations.length === 0) return null;
    
    const hasConfirmed = customerReservations.some(r => r.status === 'confirmed' || r.status === 'pago_total');
    const hasPending = customerReservations.some(r => r.status === 'pending' || r.status === 'pendente');
    const hasPartial = customerReservations.some(r => r.status === 'pago_parcial');
    
    if (hasConfirmed && !hasPending && !hasPartial) return 'pago';
    if (hasPartial) return 'parcial';
    if (hasPending) return 'pendente';
    return 'pago';
  };

  // Buscar cidade de embarque da última reserva do cliente
  const getCustomerBoarding = (cpf) => {
    const customerReservations = getCustomerReservations(cpf);
    if (customerReservations.length === 0) return null;
    
    // Pegar a reserva mais recente
    const latestReservation = customerReservations[0];
    return latestReservation.city_departure || latestReservation.boarding_point || null;
  };

  const formatCPF = (cpf) => {
    if (!cpf) return '';
    const numbers = cpf.replace(/\D/g, '');
    if (numbers.length === 11) {
      return `${numbers.slice(0,3)}.${numbers.slice(3,6)}.${numbers.slice(6,9)}-${numbers.slice(9)}`;
    }
    return cpf;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const handleVerifyEmail = async (customer) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/customers/admin/verify-email/${customer.cpf}`);
      setMessage({ type: 'success', text: response.data.message });
      setConfirmModal({ show: false, customer: null, action: '' });
      await loadData();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erro ao verificar e-mail.' });
    }
  };

  const handleDeleteCustomer = async (customer) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/customers/admin/${customer.cpf}`);
      setMessage({ type: 'success', text: 'Cliente excluído com sucesso!' });
      setConfirmModal({ show: false, customer: null, action: '' });
      await loadData();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erro ao excluir cliente.' });
    }
  };

  const filteredCustomers = customers.filter(c => {
    const search = searchTerm.toLowerCase();
    return (
      c.full_name?.toLowerCase().includes(search) ||
      c.email?.toLowerCase().includes(search) ||
      c.cpf?.includes(search) ||
      c.phone?.includes(search)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#003366]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#003366] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/admin/dashboard" className="hover:text-[#FFD700]">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <Users className="w-6 h-6" />
            <h1 className="text-xl font-bold">Gerenciar Clientes</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={loadData} variant="outline" size="sm" className="text-white border-white hover:bg-white/10">
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
            <Button
              onClick={async () => {
                if (!window.confirm('Sincronizar todos os CPFs de reservas com Gerenciar Clientes? Não sobrescreve senhas existentes.')) return;
                try {
                  const r = await axios.post(`${BACKEND_URL}/api/customers/admin/backfill-from-reservations`);
                  alert(`Sync concluído. Criados: ${r.data.created}, atualizados: ${r.data.updated}, pulados: ${r.data.skipped}.`);
                  loadData();
                } catch (e) { alert('Erro: ' + (e.response?.data?.detail || e.message)); }
              }}
              variant="outline"
              size="sm"
              className="text-white border-white hover:bg-white/10"
              data-testid="backfill-customers-btn"
            >
              Sincronizar Compras
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Mensagem */}
        {message.text && (
          <div className={`mb-4 p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
            {message.text}
            <button onClick={() => setMessage({ type: '', text: '' })} className="ml-auto">×</button>
          </div>
        )}

        {/* Alerta de E-mail */}
        {showEmailWarning && emailStatus?.is_test_mode && (
          <Card className="mb-6 border-yellow-300 bg-yellow-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="font-bold text-yellow-800 mb-2">⚠️ Sistema de E-mail em Modo de Teste</h3>
                  <p className="text-yellow-700 text-sm mb-2">
                    O sistema está usando <code className="bg-yellow-200 px-1 rounded">{emailStatus.sender_email}</code> que é apenas para testes.
                  </p>
                  <p className="text-yellow-700 text-sm mb-3">
                    <strong>Problema:</strong> Os e-mails de confirmação de cadastro <strong>NÃO serão entregues</strong> aos clientes reais.
                  </p>
                  
                  {/* Formulário de Configuração */}
                  <div className="bg-white p-4 rounded border border-yellow-300 mb-3">
                    <p className="font-semibold text-gray-800 mb-3">Configurar Credenciais de E-mail:</p>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm text-gray-600">E-mail de Envio:</label>
                        <input
                          type="email"
                          placeholder="contato@toindoali.com.br"
                          className="w-full p-2 border rounded mt-1"
                          id="email-config-sender"
                          defaultValue="contato@toindoali.com.br"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">Chave API do Resend:</label>
                        <input
                          type="password"
                          placeholder="re_xxxxx..."
                          className="w-full p-2 border rounded mt-1"
                          id="email-config-key"
                        />
                      </div>
                      <Button 
                        onClick={async () => {
                          const sender = document.getElementById('email-config-sender').value;
                          const apiKey = document.getElementById('email-config-key').value;
                          if (!sender || !apiKey) {
                            alert('Preencha todos os campos');
                            return;
                          }
                          try {
                            const res = await axios.post(`${BACKEND_URL}/api/customers/admin/email-config`, {
                              sender_email: sender,
                              resend_api_key: apiKey
                            });
                            if (res.data.success) {
                              alert('Configurações salvas com sucesso!');
                              loadData();
                            }
                          } catch (e) {
                            alert('Erro ao salvar: ' + (e.response?.data?.detail || e.message));
                          }
                        }}
                        className="w-full bg-green-600 hover:bg-green-700 text-white"
                      >
                        Salvar Configurações
                      </Button>
                    </div>
                  </div>
                  
                  <p className="text-yellow-700 text-sm">
                    <strong>Solução temporária:</strong> Use o botão "Verificar E-mail" abaixo para liberar manualmente o acesso de novos clientes.
                  </p>
                </div>
                <button onClick={() => setShowEmailWarning(false)} className="text-yellow-600 hover:text-yellow-800">×</button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Estatísticas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <Users className="w-8 h-8 mx-auto mb-2 text-blue-600" />
              <p className="text-2xl font-bold">{customers.length}</p>
              <p className="text-sm text-gray-600">Total de Clientes</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-600" />
              <p className="text-2xl font-bold">{customers.filter(c => c.email_verified).length}</p>
              <p className="text-sm text-gray-600">E-mails Verificados</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <XCircle className="w-8 h-8 mx-auto mb-2 text-red-600" />
              <p className="text-2xl font-bold">{customers.filter(c => !c.email_verified).length}</p>
              <p className="text-sm text-gray-600">Aguardando Verificação</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Calendar className="w-8 h-8 mx-auto mb-2 text-purple-600" />
              <p className="text-2xl font-bold">
                {customers.filter(c => {
                  const created = new Date(c.created_at);
                  const now = new Date();
                  return (now - created) < 7 * 24 * 60 * 60 * 1000;
                }).length}
              </p>
              <p className="text-sm text-gray-600">Novos (7 dias)</p>
            </CardContent>
          </Card>
        </div>

        {/* Busca */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Buscar por nome, e-mail, CPF ou telefone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Lista de Clientes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Clientes Cadastrados ({filteredCustomers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3 font-semibold">Cliente</th>
                    <th className="text-left p-3 font-semibold">Contato</th>
                    <th className="text-center p-3 font-semibold">Status E-mail</th>
                    <th className="text-center p-3 font-semibold">Pagamento</th>
                    <th className="text-left p-3 font-semibold">Embarque</th>
                    <th className="text-left p-3 font-semibold">Cadastro</th>
                    <th className="text-center p-3 font-semibold">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map((customer) => {
                    const paymentStatus = getCustomerPaymentStatus(customer.cpf);
                    const boardingInfo = getCustomerBoarding(customer.cpf);
                    
                    return (
                    <tr key={customer.cpf} className="border-b hover:bg-gray-50">
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#003366] flex items-center justify-center text-white font-bold">
                            {customer.full_name?.charAt(0).toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className="font-medium">{customer.full_name}</p>
                            <p className="text-sm text-gray-500 flex items-center gap-1">
                              <CreditCard className="w-3 h-3" />
                              {formatCPF(customer.cpf)}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="p-3">
                        <p className="text-sm flex items-center gap-1">
                          <Mail className="w-3 h-3 text-gray-400" />
                          {customer.email}
                        </p>
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {customer.phone || '-'}
                        </p>
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex flex-col gap-1 items-center">
                          {customer.email_verified ? (
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Verificado
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800">
                              <XCircle className="w-3 h-3 mr-1" />
                              Pendente
                            </Badge>
                          )}
                          {customer.password_set === false && (
                            <Badge className="bg-amber-100 text-amber-800 text-[10px]" data-testid={`customer-nopw-${customer.cpf}`} title="Cliente veio de compra mas ainda não definiu senha. Ele deve usar 'Esqueci minha senha' para receber o link.">
                              Sem senha
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        {paymentStatus === 'pago' ? (
                          <Badge className="bg-green-100 text-green-800">Pago</Badge>
                        ) : paymentStatus === 'parcial' ? (
                          <Badge className="bg-blue-100 text-blue-800">Parcial</Badge>
                        ) : paymentStatus === 'pendente' ? (
                          <Badge className="bg-yellow-100 text-yellow-800">Pendente</Badge>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="p-3">
                        <p className="text-sm text-gray-600">{boardingInfo || '-'}</p>
                      </td>
                      <td className="p-3">
                        <p className="text-sm">{formatDate(customer.created_at)}</p>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center justify-center gap-2">
                          {!customer.email_verified && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 border-green-300 hover:bg-green-50"
                              onClick={() => setConfirmModal({ show: true, customer, action: 'verify' })}
                              title="Verificar e-mail manualmente"
                            >
                              <ShieldCheck className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => setConfirmModal({ show: true, customer, action: 'delete' })}
                            title="Excluir cliente"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
              
              {filteredCustomers.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhum cliente encontrado.</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modal de Confirmação */}
      <Dialog open={confirmModal.show} onOpenChange={(v) => !v && setConfirmModal({ show: false, customer: null, action: '' })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {confirmModal.action === 'verify' ? '✅ Verificar E-mail' : '🗑️ Excluir Cliente'}
            </DialogTitle>
          </DialogHeader>
          
          {confirmModal.customer && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium">{confirmModal.customer.full_name}</p>
                <p className="text-sm text-gray-600">{confirmModal.customer.email}</p>
                <p className="text-sm text-gray-600">CPF: {formatCPF(confirmModal.customer.cpf)}</p>
              </div>
              
              {confirmModal.action === 'verify' ? (
                <div className="p-3 bg-green-50 rounded-lg text-green-800 text-sm">
                  <p>Ao verificar manualmente, o cliente poderá acessar sua área mesmo sem ter recebido o e-mail de confirmação.</p>
                </div>
              ) : (
                <div className="p-3 bg-red-50 rounded-lg text-red-800 text-sm">
                  <p>⚠️ Esta ação é irreversível. O cliente será removido permanentemente do sistema.</p>
                </div>
              )}
              
              <div className="flex gap-2 pt-4">
                <Button variant="outline" onClick={() => setConfirmModal({ show: false, customer: null, action: '' })} className="flex-1">
                  Cancelar
                </Button>
                <Button
                  onClick={() => confirmModal.action === 'verify' 
                    ? handleVerifyEmail(confirmModal.customer) 
                    : handleDeleteCustomer(confirmModal.customer)
                  }
                  className={`flex-1 ${confirmModal.action === 'verify' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                >
                  {confirmModal.action === 'verify' ? 'Verificar E-mail' : 'Excluir'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCustomers;
